import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  useGetProfile,
  customFetch,
  type ProfilePreferences,
} from "@workspace/api-client-react";
import { useAuth } from "@clerk/react";
import { useQueryClient } from "@tanstack/react-query";
import {
  ACCENTS,
  ACCENT_BY_ID,
  FONT_PAIRINGS,
  FONT_PAIRING_BY_ID,
  PATTERN_BY_ID,
  SKINS,
  SKIN_BY_ID,
} from "./catalog";
import {
  DEFAULT_APPEARANCE,
  type AppearancePrefs,
  type Mode,
  type ResolvedMode,
  type SkinTokens,
} from "./types";

// Per-user storage isolates teachers sharing a browser; the unscoped
// key is a cross-tab mirror only.
const STORAGE_KEY_FALLBACK = "classtrack:appearance";
const userKey = (userId: string) => `classtrack:appearance:${userId}`;

function readAppearanceFromPreferences(
  preferences: ProfilePreferences | undefined,
): Partial<AppearancePrefs> | undefined {
  if (!preferences) return undefined;
  const candidate = (preferences as Record<string, unknown>)["appearance"];
  if (!candidate || typeof candidate !== "object") return undefined;
  return candidate as Partial<AppearancePrefs>;
}

function readFromStorage(key: string): AppearancePrefs | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    return sanitise(JSON.parse(raw) as Partial<AppearancePrefs>);
  } catch {
    return null;
  }
}

function writeToStorage(key: string, prefs: AppearancePrefs): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, JSON.stringify(prefs));
  } catch { /* noop */ }
}

function readCachedAppearance(): AppearancePrefs {
  return readFromStorage(STORAGE_KEY_FALLBACK) ?? DEFAULT_APPEARANCE;
}

function sanitise(input: Partial<AppearancePrefs> | null | undefined): AppearancePrefs {
  const skin = input?.skin && SKIN_BY_ID[input.skin] ? input.skin : DEFAULT_APPEARANCE.skin;
  const accent = input?.accent && ACCENT_BY_ID[input.accent] ? input.accent : SKIN_BY_ID[skin].defaultAccent;
  const fontPairing =
    input?.fontPairing && FONT_PAIRING_BY_ID[input.fontPairing]
      ? input.fontPairing
      : DEFAULT_APPEARANCE.fontPairing;
  const pattern = input?.pattern && PATTERN_BY_ID[input.pattern] ? input.pattern : DEFAULT_APPEARANCE.pattern;
  const mode: Mode = input?.mode === "dark" || input?.mode === "system" || input?.mode === "light" ? input.mode : "light";
  return { skin, mode, accent, fontPairing, pattern };
}

function resolveMode(mode: Mode): ResolvedMode {
  if (mode !== "system") return mode;
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

const loadedFontUrls = new Set<string>();
function ensureFontPairing(id: string): void {
  const pairing = FONT_PAIRING_BY_ID[id];
  if (!pairing?.googleFontsUrl || loadedFontUrls.has(pairing.googleFontsUrl)) return;
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = pairing.googleFontsUrl;
  link.dataset.appearanceFont = pairing.id;
  document.head.appendChild(link);
  loadedFontUrls.add(pairing.googleFontsUrl);
}

// Apply prefs to <html>: CSS vars, data-* attrs, .dark toggle, fonts.
export function applyAppearance(prefs: AppearancePrefs): void {
  if (typeof document === "undefined") return;
  const skin = SKIN_BY_ID[prefs.skin] ?? SKIN_BY_ID[DEFAULT_APPEARANCE.skin];
  const accent = ACCENT_BY_ID[prefs.accent] ?? ACCENT_BY_ID[skin.defaultAccent];
  const fontPairing = FONT_PAIRING_BY_ID[prefs.fontPairing] ?? FONT_PAIRING_BY_ID[DEFAULT_APPEARANCE.fontPairing];
  const pattern = PATTERN_BY_ID[prefs.pattern] ?? PATTERN_BY_ID[DEFAULT_APPEARANCE.pattern];
  const resolvedMode = resolveMode(prefs.mode);

  const tokens: SkinTokens = resolvedMode === "dark" ? skin.dark : skin.light;
  const html = document.documentElement;

  for (const [key, value] of Object.entries(tokens)) {
    html.style.setProperty(`--${key}`, value);
  }

  // Accent overrides primary + sidebar-primary so buttons and active nav match.
  html.style.setProperty("--primary", accent.hsl);
  html.style.setProperty("--primary-foreground", accent.foregroundHsl);
  html.style.setProperty("--primary-border", accent.borderHsl);
  html.style.setProperty("--ring", accent.hsl);
  html.style.setProperty("--sidebar-primary", accent.hsl);
  html.style.setProperty("--sidebar-primary-foreground", accent.foregroundHsl);
  html.style.setProperty("--sidebar-primary-border", accent.borderHsl);
  html.style.setProperty("--sidebar-ring", accent.hsl);

  html.style.setProperty("--app-font-sans", fontPairing.body);
  html.style.setProperty("--app-font-serif", fontPairing.heading);
  html.style.setProperty("--app-font-heading", fontPairing.heading);
  ensureFontPairing(fontPairing.id);

  // .app-pattern-layer (inside <main>) reads these vars — see index.css.
  html.style.setProperty("--app-pattern-image", pattern.url || "none");
  html.style.setProperty("--app-pattern-color", `hsl(var(--foreground))`);

  html.dataset.skin = skin.id;
  html.dataset.pattern = pattern.id;
  html.dataset.mode = resolvedMode;
  html.dataset.accent = accent.id;

  if (resolvedMode === "dark") {
    html.classList.add("dark");
  } else {
    html.classList.remove("dark");
  }
}

type AppearanceContextValue = {
  prefs: AppearancePrefs;
  resolvedMode: ResolvedMode;
  update: (patch: Partial<AppearancePrefs>) => void;
  reset: () => void;
};

const AppearanceContext = createContext<AppearanceContextValue | null>(null);

// Sits above ClerkProvider so Clerk's appearance prop reacts to accent
// changes. Seeds from the unscoped cache to avoid a theme flash on
// returning visits; AppearanceServerSync then swaps in the per-user
// value (or DEFAULT_APPEARANCE for signed-out) once Clerk loads.
export function AppearanceProvider({ children }: { children: React.ReactNode }) {
  // Boot from DEFAULT_APPEARANCE so the pre-auth/signed-out paint is
  // deterministically Classic Teal. AppearanceServerSync hydrates the
  // per-user cache (and then the server value) once Clerk knows who's
  // signed in; useLayoutEffect avoids a flash on that swap.
  const [prefs, setPrefs] = useState<AppearancePrefs>(DEFAULT_APPEARANCE);
  const [resolvedMode, setResolvedMode] = useState<ResolvedMode>(() => resolveMode(prefs.mode));

  // Apply synchronously before paint to eliminate the flash between the
  // index.html prepaint defaults and the React-resolved appearance.
  useLayoutEffect(() => {
    applyAppearance(prefs);
    setResolvedMode(resolveMode(prefs.mode));
  }, [prefs]);

  useEffect(() => {
    if (prefs.mode !== "system" || typeof window === "undefined") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => {
      applyAppearance(prefs);
      setResolvedMode(resolveMode(prefs.mode));
    };
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [prefs]);

  const update = useCallback((patch: Partial<AppearancePrefs>) => {
    setPrefs((prev) => {
      const merged = sanitise({ ...prev, ...patch });
      writeToStorage(STORAGE_KEY_FALLBACK, merged);
      return merged;
    });
  }, []);

  const reset = useCallback(() => {
    writeToStorage(STORAGE_KEY_FALLBACK, DEFAULT_APPEARANCE);
    setPrefs(DEFAULT_APPEARANCE);
  }, []);

  const value = useMemo<AppearanceContextValue>(
    () => ({ prefs, resolvedMode, update, reset }),
    [prefs, resolvedMode, update, reset],
  );

  return <AppearanceContext.Provider value={value}>{children}</AppearanceContext.Provider>;
}

// Renders inside ClerkProvider + QueryClientProvider. Hydrates from the
// user's profile, persists local edits back (debounced), and resets to
// defaults on sign-out so a previous user's skin never bleeds through.
export function AppearanceServerSync(): null {
  const ctx = useContext(AppearanceContext);
  const queryClient = useQueryClient();
  const { userId, isLoaded: authLoaded, isSignedIn } = useAuth();
  const { data: profile } = useGetProfile({
    query: { enabled: Boolean(authLoaded && isSignedIn && userId) },
  });
  const seededForUserRef = useRef<string | null>(null);
  const lastSeededKeyRef = useRef<string | null>(null);
  const persistTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastPersistedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!ctx || !authLoaded) return;
    // Use a string sentinel so the initial signed-out case (`null`) is
    // distinguishable from "not yet seeded".
    const key = userId ?? "__signed_out__";
    if (lastSeededKeyRef.current === key) return;
    lastSeededKeyRef.current = key;
    seededForUserRef.current = null;
    lastPersistedRef.current = null;
    if (userId) {
      const cached = readFromStorage(userKey(userId));
      ctx.update(cached ?? DEFAULT_APPEARANCE);
    } else {
      // Signed-out: always Classic Teal, never inherit a previous
      // teacher's skin from the unscoped cache.
      ctx.update(DEFAULT_APPEARANCE);
    }
  }, [ctx, authLoaded, userId]);

  useEffect(() => {
    if (!ctx || !profile || !isSignedIn || !userId) return;
    if (seededForUserRef.current === userId) return;
    seededForUserRef.current = userId;
    const serverPrefs = readAppearanceFromPreferences(profile.preferences);
    const next = serverPrefs ? sanitise(serverPrefs) : DEFAULT_APPEARANCE;
    lastPersistedRef.current = JSON.stringify(next);
    writeToStorage(userKey(userId), next);
    writeToStorage(STORAGE_KEY_FALLBACK, next);
    ctx.update(next);
  }, [ctx, profile, isSignedIn, userId]);

  useEffect(() => {
    if (!ctx || !profile || !userId) return;
    const serialized = JSON.stringify(ctx.prefs);
    if (serialized === lastPersistedRef.current) return;

    writeToStorage(userKey(userId), ctx.prefs);
    writeToStorage(STORAGE_KEY_FALLBACK, ctx.prefs);

    if (persistTimer.current) clearTimeout(persistTimer.current);
    const snapshot = ctx.prefs;
    persistTimer.current = setTimeout(async () => {
      try {
        // Send only the appearance slice; the server's profile PUT
        // shallow-merges into existing preferences so we don't risk
        // racing other surfaces by echoing back a stale full bag.
        await customFetch("/api/profile", {
          method: "PUT",
          body: JSON.stringify({
            preferences: { appearance: snapshot },
          }),
        });
        lastPersistedRef.current = JSON.stringify(snapshot);
        queryClient.invalidateQueries({ queryKey: ["/api/profile"] });
      } catch {
        // Non-fatal — the local cache still drives the UI.
      }
    }, 400);
    return () => {
      if (persistTimer.current) clearTimeout(persistTimer.current);
    };
  }, [ctx, ctx?.prefs, profile, userId, queryClient]);

  return null;
}

export function useAppearance(): AppearanceContextValue {
  const ctx = useContext(AppearanceContext);
  if (!ctx) {
    const prefs = readCachedAppearance();
    return {
      prefs,
      resolvedMode: resolveMode(prefs.mode),
      update: () => undefined,
      reset: () => undefined,
    };
  }
  return ctx;
}

export { ACCENTS, FONT_PAIRINGS, SKINS };
